import React, { useRef, useState } from 'react';
import { Check } from 'lucide-react';
import classesImg from '../../assets/images/classes.png';
import reportImg from '../../assets/images/report.png';
import messageImg from '../../assets/images/message.png';
import useReveal, { reveal } from '../../hooks/useReveal';
import { APP_LINKS } from '../../data/site';

const VIEWS = [
  {
    id: 'classes',
    tab: 'Classes',
    title: 'Every class, teacher and roster in one list',
    body:
      'Set up classes for the term, assign teachers, and see enrolment and progress at a glance. Search by class, teacher, term or year.',
    image: classesImg,
    width: 2304,
    height: 1310,
    alt:
      'The AtadaBoard classes screen listing classes with teacher, term, year, member count and completion status',
    path: '/classes',
    points: ['Termly class setup', 'Teacher assignment', 'Enrolment at a glance'],
  },
  {
    id: 'reports',
    tab: 'Reports',
    title: 'Report cards built from the term you actually taught',
    body:
      'Scores, attendance and remarks come together on one student record. Export the report or publish it straight to the parent.',
    image: reportImg,
    width: 2080,
    height: 1234,
    alt:
      'A student report in AtadaBoard showing test scores, days present and absent, teacher remarks and homework history',
    path: '/students/report',
    points: ['Custom report templates', 'Attendance included', 'Published to parents'],
  },
  {
    id: 'messages',
    tab: 'Messages',
    title: 'Talk to parents without a WhatsApp group',
    body:
      'Direct and class conversations that stay with the school. Send notices, images, video and PDFs, and keep the whole thread on record.',
    image: messageImg,
    width: 2248,
    height: 1282,
    alt:
      'The AtadaBoard messages screen showing a conversation between a teacher and a parent about their child',
    path: '/messages',
    points: ['Direct and class threads', 'Files, images and video', 'Kept on the record'],
  },
];

const ProductShowcase = () => {
  const [active, setActive] = useState(0);
  const [ref, isVisible] = useReveal();
  const tabRefs = useRef([]);

  /* Arrow keys move between tabs, as expected of a tablist. */
  const handleKeyDown = (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const next =
      event.key === 'ArrowRight'
        ? (active + 1) % VIEWS.length
        : (active - 1 + VIEWS.length) % VIEWS.length;
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  const view = VIEWS[active];

  return (
    <section id="platform" className="ab-section ab-showcase" ref={ref}>
      <div className="ab-shell">
        <header className={`ab-showcase__head ${reveal(isVisible)}`}>
          <h2 className="ab-h2">School management made easy</h2>
          <p className="ab-lead">
            The same platform your administrators, teachers and parents open every morning.
          </p>
        </header>

        <div
          className={`ab-showcase__tabs ${reveal(isVisible)}`}
          role="tablist"
          aria-label="AtadaBoard screens"
          onKeyDown={handleKeyDown}
          style={{ '--ab-reveal-delay': '80ms' }}
        >
          {VIEWS.map((item, index) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              id={`ab-tab-${item.id}`}
              aria-selected={index === active}
              aria-controls={`ab-panel-${item.id}`}
              tabIndex={index === active ? 0 : -1}
              ref={(node) => {
                tabRefs.current[index] = node;
              }}
              className={`ab-showcase__tab${index === active ? ' is-active' : ''}`}
              onClick={() => setActive(index)}
            >
              {item.tab}
            </button>
          ))}
        </div>

        <div
          className={`ab-showcase__panel ${reveal(isVisible)}`}
          role="tabpanel"
          id={`ab-panel-${view.id}`}
          aria-labelledby={`ab-tab-${view.id}`}
          style={{ '--ab-reveal-delay': '140ms' }}
        >
          <div className="ab-frame ab-showcase__frame">
            <div className="ab-frame__bar">
              <div className="ab-frame__dots">
                <span />
                <span />
                <span />
              </div>
              <div className="ab-frame__url">
                {APP_LINKS.web.replace('https://', '')}
                {view.path}
              </div>
            </div>
            <img
              key={view.id}
              src={view.image}
              alt={view.alt}
              width={view.width}
              height={view.height}
              className="ab-frame__media ab-showcase__shot"
              loading="lazy"
              decoding="async"
            />
          </div>

          <div className="ab-showcase__caption">
            <h3 className="ab-h3">{view.title}</h3>
            <p className="ab-body">{view.body}</p>
            <ul className="ab-showcase__points">
              {view.points.map((point) => (
                <li key={point}>
                  <Check size={15} strokeWidth={2.5} />
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <style jsx>{`
        .ab-showcase__head { margin-bottom: 2rem; }
        .ab-showcase__head .ab-lead { margin-top: 0.875rem; }

        .ab-showcase__tabs {
          display: flex;
          gap: 0.375rem;
          padding: 0.3125rem;
          margin-bottom: 2rem;
          background: var(--ab-paper-2);
          border: 1px solid var(--ab-line);
          border-radius: var(--ab-r-pill);
          width: fit-content;
          max-width: 100%;
          overflow-x: auto;
          scrollbar-width: none;
        }

        .ab-showcase__tabs::-webkit-scrollbar { display: none; }

        .ab-showcase__tab {
          flex-shrink: 0;
          padding: 0.625rem 1.375rem;
          border: 0;
          border-radius: var(--ab-r-pill);
          background: transparent;
          font-family: var(--ab-font-body);
          font-size: 0.9375rem;
          font-weight: 600;
          color: var(--ab-ink-2);
          cursor: pointer;
          transition: background-color 0.22s var(--ab-ease), color 0.22s var(--ab-ease),
            box-shadow 0.22s var(--ab-ease);
        }

        .ab-showcase__tab:hover { color: var(--ab-ink); }

        .ab-showcase__tab.is-active {
          background: var(--ab-paper);
          color: var(--ab-blue);
          box-shadow: var(--ab-shadow-sm);
        }

        .ab-showcase__tab:focus-visible {
          outline: 2px solid var(--ab-blue);
          outline-offset: 2px;
        }

        .ab-showcase__panel {
          display: grid;
          grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr);
          gap: clamp(1.75rem, 3.5vw, 3rem);
          align-items: center;
        }

        /* The screenshot fades in on tab change; nothing moves, so no reflow. */
        .ab-showcase__shot { animation: abShotIn 0.35s var(--ab-ease); }

        @keyframes abShotIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .ab-showcase__caption h3 { margin-bottom: 0.75rem; }

        .ab-showcase__points {
          list-style: none;
          margin: 1.5rem 0 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .ab-showcase__points li {
          display: flex;
          align-items: center;
          gap: 0.625rem;
          font-size: 0.9375rem;
          font-weight: 500;
          color: var(--ab-ink);
        }

        .ab-showcase__points svg {
          flex-shrink: 0;
          color: var(--ab-blue);
        }

        @media (max-width: 940px) {
          .ab-showcase__panel { grid-template-columns: minmax(0, 1fr); }
        }
      `}</style>
    </section>
  );
};

export default ProductShowcase;
